import React, { useState, useEffect, JSX, memo, useMemo } from 'react';
import { useMediaQuery } from '../../Styles';
import { ButtonTypes } from '../../Definitions';
import NewButton from '../../Components/BaseComponents/newButton';

// #region | CardBtn
const CardBtn = memo(({ index, cardBtn }: { index: number, cardBtn: CardsView_Props['cardBtn'] }) => {
  if (!cardBtn) return null;
  
  return (
    <NewButton
      key={`cardBtn-${index}`}
      content={cardBtn.content ?? ''}
      subContent={cardBtn?.subContent}
      onClick={cardBtn.onCardBtnClick}
      buttonType={ButtonTypes.cardButton}
      overrides={{ baseStyles: { height: cardBtn.height ?? '200px', zIndex: 0 } }}
    />
  )
})
// #endregion

interface CardsView_Props {
  items?: JSX.Element[]
  cardBtn?: {
    content?: string;
    subContent?: string;
    onCardBtnClick?: () => void;
    height?: string;
  };
}

export default function CardsView({ items, cardBtn }: CardsView_Props) {
  const [columns, setColumns] = useState(1);

  // #region | Columns count based on screen width
  const columnCount4 = useMediaQuery(`(min-width: 1700px)`);
  const columnCount3 = useMediaQuery(`(min-width: 1360px)`);
  const columnCount2 = useMediaQuery(`(min-width: 700px)`);

  useEffect(() => {
    const getColumnCount = () => {
      if (columnCount4) return 4;
      if (columnCount3) return 3;
      if (columnCount2) return 2;
      return 1;
    };
    setColumns(getColumnCount());
  }, [columnCount2, columnCount3, columnCount4]);
  // #endregion

  // Cards container height  
  let _containerHeight = useMemo(() => {
    const cardHeight = 250;
    const itemsInCol = cardBtn?.content !== undefined ? (items?.length ?? 0) + 1 : items?.length
    const extra = columns === 1 ? 400 : 300
    
    if (itemsInCol === undefined) return 0;

    return `${Math.ceil(itemsInCol / columns) * cardHeight + extra}px`
  }, [cardBtn?.content, columns, items?.length])
  
  // #region |  View
  const view = useMemo(() => {
    if (!items) return null;

    const _columnWidth = `calc(${100 / columns}% - ${(20 * (columns - 1)) / columns}px)`;

    const columnsData: JSX.Element[][] = Array.from({ length: columns }, () => []);

    if (cardBtn?.onCardBtnClick) {
      columnsData[0].push(null as unknown as JSX.Element);
    }

    items.forEach((item, index) => {
      const colIndex = (index + (cardBtn?.onCardBtnClick ? 1 : 0)) % columns;
      columnsData[colIndex].push(item);
    });

    return columnsData.map((colItems, colIndex) => (
      <div
        key={colIndex}
        style={{ display: "flex", flexDirection: "column", gap: "20px", width: _columnWidth }}
      >
        {colItems.map((item, index) => {
          if (item === null && cardBtn?.onCardBtnClick) {
            return <CardBtn key={`cardBtn-${index}`} index={index} cardBtn={cardBtn} />;
          }
          return <div key={`item-${index}`} style={{zIndex: "99999"}}>{item}</div>;
        })}
      </div>
    ));
  }, [cardBtn, columns, items]);
  // #endregion

  return (
    <div style={{ display: "flex", gap: "20px", width: "100%", alignItems: "start", height: _containerHeight }}>
      {view}
    </div>
  )
}